/* eslint-disable react/no-danger */
import React from 'react'
import classes from '@src/styles/disposal/pitch.module.css'
import classNames from 'classnames'
import QuoteRequestFormV2 from '@src/components/QuoteRequestFormV2'
import useLocalizedCity from '@src/hooks/useLocalizedCity'
import useAppLink from '@src/hooks/useAppLink'
import { getAnonymousId, segmentTrack } from '@src/functions/segment'
import pitchHeroImages, {
  defaultPitchHeroImage,
  PitchHeroImageKey,
  PitchHeroImageOverride,
} from '@src/constants/pitchHeroImages'

export type PitchProps = {
  heading: string
  pitchText: Array<string>
  material: string
  ctaLink?: string
  ctaText?: string
  variant?: 'default' | 'hazardous-waste'
  /**
   * Which photo fills the right half of the hero in the `hazardous-waste`
   * variant. Keys and their crops live in src/constants/pitchHeroImages.ts.
   * Omit for the default hazardous waste photo.
   */
  heroImage?: PitchHeroImageKey
  /**
   * Per-page correction to the crop of `heroImage`, e.g. `{ size: 'auto 130%' }`
   * for a page whose hero is shorter than the rest of its group and so renders
   * the photo smaller. Only for genuine one-offs — anything shared belongs in
   * the registry entry.
   */
  heroImageOverride?: PitchHeroImageOverride
  /**
   * Optional content rendered in the right-hand hero column in place of the
   * default CTA card / quote form (e.g. the n8n chatbot embed). When set, it
   * takes precedence over `ctaLink` and the quote form.
   */
  heroAside?: React.ReactNode
  /**
   * Extra class for the right-hand hero column wrapper. Used by pages that need to
   * reserve a fixed height there — e.g. while an A/B test swaps `heroAside` in
   * client-side and a shifting hero would cost CLS.
   */
  ctaContainerClassName?: string
  /**
   * Optional override for the CTA card heading. Defaults to the
   * `material`-driven "Ready to get rid of your {material}?".
   */
  ctaHeading?: string
  /**
   * Optional override for the CTA card subtext. Defaults to the instant
   * Mirror Mirror quote copy, which only holds for self-serve services —
   * manual-quote services (certified destruction, trash valet) pass their own.
   */
  ctaSubtext?: string
  /**
   * Optional content rendered in a second white card directly below the CTA
   * card (only in the `ctaLink` layout), e.g. a Trustpilot rating widget.
   */
  belowCta?: React.ReactNode
  /**
   * Optional content rendered below the hero intro copy in the left column,
   * e.g. the "Trusted by 10,000+ clients" trust block.
   */
  belowPitch?: React.ReactNode
  /**
   * Opts this page out of `?location=` city localization. Set it wherever a
   * localized headline would be wrong rather than merely generic:
   *  - the city landing pages, which are already localized (CityServicePage)
   *  - pages whose heading already names its own geography, e.g. Austin
   *    composting or Southern California fire debris, where appending a second
   *    city produces nonsense.
   * See src/hooks/useLocalizedCity.ts.
   */
  disableCityLocalization?: boolean
}

function Pitch({
  heading,
  pitchText,
  material,
  ctaLink,
  ctaText,
  variant = 'default',
  heroImage = defaultPitchHeroImage,
  heroImageOverride,
  ctaHeading,
  ctaSubtext,
  heroAside,
  ctaContainerClassName,
  belowCta,
  belowPitch,
  disableCityLocalization,
}: PitchProps) {
  // Paid traffic arrives with ?location=<Google Ads geo target criteria ID>. When
  // that maps to a city we localize for, append it to the headline. The heading is
  // complete and correct without a city, so the worst case is a short append and
  // never a content swap — see useLocalizedCity for why this is client-side only,
  // and why it runs in a layout effect. The canonical tag every page hardcodes
  // keeps the param from being indexed as a duplicate; do not make it dynamic.
  const city = useLocalizedCity({ disabled: disableCityLocalization })
  // ctaLink is a static app URL in the page's content file, so the visitor's
  // gclid/utm_* have to be stamped on at click time. See useAppLink.
  const appLink = useAppLink()

  // The hand-off into the booking app, and the control side of the
  // /paint-disposal and /oil-recycling chatbot tests: the treatment's funnel is the
  // `Chat Quote - *` events, the control's is this click. Joined on `anonymousId`, the same key
  // `Experiment Viewed` carries — see useExperimentVariant. Tracking fires before
  // the browser follows the link; amplitudeTrack flushes on the spot rather than
  // batching, so the request is already in flight (with keepalive) when the
  // document unloads. See src/functions/amplitude.ts.
  const handleCtaClick = React.useCallback(() => {
    segmentTrack('Clicked Get Quote - Pitch', {
      material,
      ctaText: ctaText || 'Get a Free Quote',
      destination: ctaLink,
      anonymousId: getAnonymousId(),
    })
  }, [material, ctaText, ctaLink])

  const localizedHeading = city ? `${heading} in ${city}` : heading
  const isHazardousWaste = variant === 'hazardous-waste'
  const hero = isHazardousWaste
    ? { ...pitchHeroImages[heroImage], ...heroImageOverride }
    : undefined
  return (
    <section
      className={classNames(
        classes.mainSection,
        isHazardousWaste && classes.mainSectionHazardousWaste,
      )}
      style={
        hero
          ? ({
              '--pitch-hero-image': `url('${hero.url}')`,
              '--pitch-hero-size': hero.size,
              '--pitch-hero-position': hero.position,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div className={classes.commonSec}>
        <div className={classNames(classes.container, classes.headerContainer)}>
          <div className={classes.secHead}>
            <h1
              className={classNames(
                classes.secTitle,
                isHazardousWaste && classes.secTitleHazardousWaste,
              )}
              dangerouslySetInnerHTML={{ __html: localizedHeading }}
            />
          </div>
        </div>
        <div className={classNames(classes.container, classes.contentContainer)}>
          <div
            className={classNames(
              classes.mainContentDescription,
              isHazardousWaste && classes.mainContentDescriptionHazardousWaste,
            )}
          >
            {pitchText.map((text) => (
              <p
                key={text}
                className={classNames(
                  classes.textLg,
                  isHazardousWaste && classes.textLgHazardousWaste,
                )}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            ))}
            {belowPitch}
          </div>
          {/* Three-way JSX branch; splitting it out would not make it clearer. */}
          {/* eslint-disable-next-line no-nested-ternary */}
          {heroAside ? (
            <div
              className={classNames(
                classes.ctaContainer,
                isHazardousWaste && classes.ctaContainerHazardousWaste,
                ctaContainerClassName,
              )}
            >
              {heroAside}
            </div>
          ) : ctaLink ? (
            <div
              className={classNames(
                classes.ctaContainer,
                isHazardousWaste && classes.ctaContainerHazardousWaste,
                ctaContainerClassName,
              )}
            >
              <div className={classes.ctaStack}>
                <div className={classes.ctaCard}>
                  <p className={classes.ctaHeading}>
                    {ctaHeading || `Ready to get rid of your ${material}?`}
                  </p>
                  <p className={classes.ctaSubtext}>
                    {ctaSubtext ||
                      'Get an instant price quote powered by our Mirror Mirror AI. Most orders are priced in minutes.'}
                  </p>
                  <a href={appLink(ctaLink)} className={classes.ctaButton} onClick={handleCtaClick}>
                    {ctaText || 'Get a Free Quote'}
                  </a>
                </div>
                {belowCta ? <div className={classes.ctaCardSecondary}>{belowCta}</div> : null}
              </div>
            </div>
          ) : (
            <QuoteRequestFormV2 isPallets={material === 'pallets'} />
          )}
        </div>
      </div>
    </section>
  )
}

export default Pitch
